// Lyra(궁수) 연출 묶음(c022 D4a): 모듈 라이브러리(VfxModules)로 큐 조합을 띄우고, 화살 렌더러(ArrowRenderer)로 투사체와 화살비 낙하를 그린다.
// 렌더 연결(지휘가 한다):
//   const lyra = new LyraFx(vfxModules); scene.add(lyra.group);
//   Ev.Arrow → lyra.arrows.fire(x, y, z, dirX, dirZ, speed, range, kind)
//   Ev.SkillCue(vfx 채널, Lyra 큐 이름) → lyra.playCue(name, heroX, heroY, heroZ, facing, lag)  (중심을 아는 큐는 ahead 인자로 덮어쓴다)
//   관통 판정(Ev.Hit, 관통 화살) → lyra.pierceHit(x, y, z, dirYaw)
//   스킬 끊김 → lyra.cutSkill(LyraSkill.*)  · 프레임마다 lyra.update(heroDt)  · 장면 전환 lyra.clear()
// 드로우콜: 화살 2(몸통·꼬리) + 쓰는 모듈은 VfxModules의 모듈당 1(새 메시 없음). 프레임 루프·이벤트 처리 안에서 새 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { ArrowRenderer, RAIN_SPEED as ARROW_RAIN_SPEED } from './arrows';
import { findLyraCue, type LyraCueEntry } from './modules/lyraCueMap';
import type { VfxModules } from './modules';

/** 화살비 지연 칸 수(각성 한 파 16발 × 겹친 파 + 천궁 14발) */
export const RAIN_PENDING_CAP = 128;

class PendingRain {
  /** 띄울 때까지 남은 시간(초) */
  t = 0;
  /** 착탄까지 남길 시간(초): 낙하 중간부터 띄워 이 시간 뒤에 바닥에 닿게 한다 */
  land = 0;
  x = 0; z = 0; height = 7; tilt = 0.2; yaw = 0;
  skill = -1;
}

export class LyraFx {
  readonly group = new THREE.Group();
  readonly arrows: ArrowRenderer;
  private readonly pending: PendingRain[] = [];
  private pendingN = 0;
  /** 화살비 위치 난수(결정적, 할당 없음) */
  private rng = 0x6d2b79f5;
  /** QA: 받은 큐 / 그린 큐 / 이름 없는 큐 / 띄운 낙하 화살 / 지연 칸이 모자라 버린 낙하 화살 */
  cues = { seen: 0, drawn: 0, unknown: 0, rainArrows: 0, rainDropped: 0 };

  constructor(private readonly mods: VfxModules, arrows = new ArrowRenderer()) {
    this.arrows = arrows;
    this.group.name = 'fx-lyra';
    this.group.add(arrows.group);
    for (let i = 0; i < RAIN_PENDING_CAP; i++) this.pending.push(new PendingRain());
  }

  private rand(): number {
    // mulberry32
    let t = (this.rng = (this.rng + 0x6d2b79f5) | 0);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** 시험·캡처용: 화살비 위치 난수를 고정한다 */
  seed(s: number): void { this.rng = s | 0 || 1; }

  /**
   * Lyra 큐 하나. ahead(m) = 중심을 facing 방향으로 옮기는 거리(생략하면 큐의 reach). lag = 이미 늦은 시간(초).
   * 반환: 표에 있는 큐면 true
   */
  playCue(name: string, x: number, y: number, z: number, facing: number, lag = 0, ahead = -1): boolean {
    this.cues.seen++;
    const e = findLyraCue(name);
    if (e === undefined) { this.cues.unknown++; return false; }
    this.playEntry(e, x, y, z, facing, lag, ahead);
    return true;
  }

  playEntry(e: LyraCueEntry, x: number, y: number, z: number, facing: number, lag = 0, ahead = -1): void {
    this.cues.drawn++;
    const d = ahead >= 0 ? ahead : e.reach;
    const cx = x + Math.sin(facing) * d, cz = z + Math.cos(facing) * d;
    this.mods.playEntry(e, cx, y, cz, facing, lag);
    const r = e.rain;
    if (r === undefined) return;
    for (let i = 0; i < r.count; i++) {
      // 원 안 고른 분포(반경 √u), 착탄 시각은 spread 안에 고르게(+ 작은 흔들림)
      const a = this.rand() * Math.PI * 2, rr = r.radius * Math.sqrt(this.rand());
      const at = r.delay + (r.count > 1 ? (i / (r.count - 1)) * r.spread : 0) * (0.85 + 0.3 * this.rand()) - lag;
      this.queueRain(cx + Math.sin(a) * rr, cz + Math.cos(a) * rr, r.height, r.tilt, facing + (this.rand() - 0.5) * 0.6, Math.max(0, at), e.skillId);
    }
  }

  /** 낙하 화살 한 발을 at초 뒤 착탄하게 예약한다(낙하 시간보다 짧으면 낙하 중간에서 띄운다) */
  private queueRain(x: number, z: number, height: number, tilt: number, yaw: number, at: number, skill: number): void {
    const fall = height / Math.cos(tilt) / ARROW_RAIN_SPEED;
    if (this.pendingN >= RAIN_PENDING_CAP) { this.cues.rainDropped++; return; }
    const q = this.pending[this.pendingN++];
    q.t = Math.max(0, at - fall);
    q.land = Math.min(fall, at);
    q.x = x; q.z = z; q.height = height; q.tilt = tilt; q.yaw = yaw; q.skill = skill;
  }

  /** 관통 화살이 몹을 꿰뚫은 자리: 작은 세로 바람 고리(링 모듈, 0.2초) */
  pierceHit(x: number, y: number, z: number, yaw: number): void {
    this.mods.play('ring', PIERCE_RING, x, y, z, yaw);
  }

  /** 스킬이 끊겼다: 아직 안 떨어진 그 스킬의 화살비를 지운다. 반환: 지운 수 */
  cutSkill(skill: number): number {
    let n = 0, i = 0;
    while (i < this.pendingN) {
      const q = this.pending[i];
      if (q.skill === skill) {
        const j = --this.pendingN;
        if (i !== j) { this.pending[i] = this.pending[j]; this.pending[j] = q; }
        n++;
        continue;
      }
      i++;
    }
    return n;
  }

  get pendingRain(): number { return this.pendingN; }

  /** dt초(영웅 시계) 진행: 지연 화살비를 띄우고 화살을 움직인다. 모듈 라이브러리 update는 렌더가 따로 부른다 */
  update(dt: number): void {
    let i = 0;
    while (i < this.pendingN) {
      const q = this.pending[i];
      q.t -= dt;
      if (q.t <= 0) {
        // 늦은 만큼(-q.t) 더 떨어진 곳에서 시작한다
        const fall = q.height / Math.cos(q.tilt) / ARROW_RAIN_SPEED;
        const lead = Math.min(fall, fall - q.land - q.t);
        this.arrows.rain(q.x, q.z, q.height, q.tilt, q.yaw, ARROW_RAIN_SPEED, lead);
        this.cues.rainArrows++;
        const j = --this.pendingN;
        if (i !== j) { this.pending[i] = this.pending[j]; this.pending[j] = q; }
        continue;
      }
      i++;
    }
    this.arrows.update(dt);
  }

  clear(): void {
    this.pendingN = 0;
    this.arrows.clear();
  }

  setReduceFlash(on: boolean): void { this.arrows.setReduceFlash(on); }

  get drawCalls(): number { return this.arrows.drawCalls; }

  dispose(): void {
    this.arrows.dispose();
    this.group.remove(this.arrows.group);
    this.pendingN = 0;
  }
}

const PIERCE_RING = { y: 1.2, length: 0.7, width: 0.14, life: 0.2, from: 0.25, color: 0x3fd0c0, color2: 0xeef7f8, intensity: 1.4, seed: 0.5 } as const;
